/**
 * Reason-capture dialog for the Block / Ignore lifecycle actions on
 * franchise_exception_tags. Used by:
 *   - the per-row drawer (single-row block + ignore - passes
 *     exceptionTypeName + itemTitle + itemSubtitle so the dialog can
 *     render an action-specific title with item context in the
 *     subtitle and a compact scope/consequence panel before submit)
 *   - the bulk action banner (bulk block + bulk ignore - passes
 *     ``items`` (the identity of each eligible row) + ``skippedCount``
 *     so the user sees exactly which exceptions change, not just a
 *     count)
 *
 * Ctrl/Cmd+Enter on the reason field submits (mirrors the drawer's
 * note composer); plain Enter keeps inserting newlines.
 *
 * Header tokens (H6 1.05rem weight 700, subtitle caption 0.78rem
 * text.secondary, DialogTitle px:2 py:1.25) are aligned with
 * `docs/plans/pending/modal-dialog-consolidation.md` so the future
 * BaseDialog migration is mechanical.
 *
 * Submit button is disabled when:
 *   - reason is empty / whitespace
 *   - reason exceeds 500 chars (matches the backend CheckConstraint
 *     ``ck_fet_block_reason_length`` and ``ck_fet_ignore_reason_length``)
 *
 * The dialog does NOT call the API itself - it returns the reason
 * via ``onSubmit`` so the parent can pick which mutation to fire
 * (single vs bulk, block vs ignore).
 */
import React, { useEffect, useId, useState } from 'react'
import {
  Box,
  TextField,
  Typography,
} from '@mui/material'
import BaseDialog from '../../../shared/dialogs/BaseDialog'
import DialogFooter from '../../../shared/dialogs/DialogFooter'

const REASON_MAX = 500
const REASON_WARN_AT = 450
// How many row identities to list before collapsing the rest into a
// "+N more" caption - keeps a large bulk selection from making the
// dialog unscrollably tall.
const ITEM_PREVIEW_CAP = 10

export type ReasonVariant = 'block' | 'ignore'

export interface ReasonDialogProps {
  open: boolean
  /** Whether this captures a block reason or an ignore reason. */
  variant: ReasonVariant
  /** How many rows the dialog will act on. Drives the title copy. */
  count: number
  /** Single-row context: exception type label (e.g. "Avg cost = $0"). */
  exceptionTypeName?: string
  /** Single-row context: item identity (e.g. "1.5 M Depth Marker"). */
  itemTitle?: string
  /** Single-row context: SKU/status/price/category identity line. */
  itemSubtitle?: string
  /**
   * Bulk-only context: the identity of each eligible row the action
   * will touch (e.g. "Sale #10293"). Listed so the user sees exactly
   * which exceptions change, not just a count. Capped for display.
   */
  items?: string[]
  /**
   * Bulk-only: how many selected rows were skipped (ineligible for the
   * chosen action). Surfaced as a caption under the list.
   */
  skippedCount?: number
  pending?: boolean
  onCancel: () => void
  onSubmit: (reason: string) => void
}

interface VariantCopy {
  titleSingle: string
  titleBulk: (n: number) => string
  bullets: [string, string]
  bulkDescription: string
  inputLabel: string
  placeholder: string
  submitLabel: string
  pendingLabel: string
}

const COPY: Record<ReasonVariant, VariantCopy> = {
  block: {
    titleSingle: 'Mark exception as blocked',
    titleBulk: (n) => `Mark ${n} exceptions as blocked`,
    bullets: [
      'This exception stays open',
      'It will be flagged as blocked until reviewed again',
    ],
    bulkDescription:
      'Blocked exceptions stay open and continue to be tracked. Capture a reason so future reviewers understand why.',
    inputLabel: 'Block reason',
    placeholder: 'Example: Waiting on FP to confirm supplier cost.',
    submitLabel: 'Mark blocked',
    pendingLabel: 'Marking...',
  },
  ignore: {
    titleSingle: 'Ignore exception',
    titleBulk: (n) => `Ignore ${n} exceptions`,
    bullets: [
      'This exception will close now',
      'This exception type will not be detected again for this item',
    ],
    bulkDescription:
      'Ignored exceptions are closed and excluded from future detection on each row. Capture a reason so future reviewers understand why.',
    inputLabel: 'Ignore reason',
    placeholder: 'Example: Known acceptable item setup for this FP.',
    submitLabel: 'Ignore exception',
    pendingLabel: 'Marking...',
  },
}

const buildTitle = (
  variant: ReasonVariant,
  count: number,
  exceptionTypeName?: string,
): string => {
  const c = COPY[variant]
  if (count > 1) return c.titleBulk(count)
  return exceptionTypeName ? `${c.titleSingle} - ${exceptionTypeName}` : c.titleSingle
}

const buildScopeLine = (exceptionTypeName?: string): string =>
  exceptionTypeName
    ? `This applies only to ${exceptionTypeName} for this item.`
    : 'This applies only to this exception.'

const ReasonDialog: React.FC<ReasonDialogProps> = ({
  open,
  variant,
  count,
  exceptionTypeName,
  itemTitle,
  itemSubtitle,
  items,
  skippedCount,
  pending = false,
  onCancel,
  onSubmit,
}) => {
  const [reason, setReason] = useState('')
  const reactId = useId()
  const inputId = `reason-${reactId}`
  const copy = COPY[variant]

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  const trimmed = reason.trim()
  const tooLong = reason.length > REASON_MAX
  const canSubmit = trimmed.length >= 1 && !tooLong && !pending

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit(reason)
  }

  const isSingleRowContext = Boolean(itemTitle)
  const title = buildTitle(variant, count, exceptionTypeName)
  const submitLabel = pending ? copy.pendingLabel : copy.submitLabel
  const headerSubtitleText = [itemTitle, itemSubtitle].filter(Boolean).join(' · ')
  const scopeLine = buildScopeLine(exceptionTypeName)

  return (
    <BaseDialog
      open={open}
      onClose={onCancel}
      title={title}
      subtitle={isSingleRowContext && headerSubtitleText ? headerSubtitleText : undefined}
      // Blocking is the heavier, attention-worthy action (amber alert); ignoring
      // is benign housekeeping (info). `variant` here is the block/ignore mode.
      variant={variant === 'block' ? 'alert' : 'info'}
      size="sm"
      dismissable={!pending}
      onSubmit={handleSubmit}
      footer={
        <DialogFooter
          cancel={{ onClick: onCancel, disabled: pending }}
          primary={{
            label: submitLabel,
            onClick: handleSubmit,
            // Button colour follows the header icon: block = amber alert,
            // ignore = neutral teal.
            tone: variant === 'block' ? 'warning' : 'neutral',
            pending,
            disabled: !canSubmit,
          }}
        />
      }
    >
        {isSingleRowContext ? (
          <Box
            sx={(theme) => ({
              bgcolor:
                variant === 'block'
                  ? 'rgba(237,108,2,0.06)'
                  : 'rgba(2,136,209,0.06)',
              border: 1,
              borderColor:
                variant === 'block'
                  ? theme.palette.warning.light
                  : theme.palette.info.light,
              borderRadius: 1,
              px: 1.5,
              py: 1,
              mb: 1.5,
            })}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {scopeLine}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mb: 0.75 }}
            >
              Related exceptions stay separate.
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              {copy.bullets.map((b) => (
                <Typography
                  key={b}
                  component="li"
                  variant="body2"
                  sx={{ color: 'text.primary', lineHeight: 1.45 }}
                >
                  {b}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {items && items.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {items.length === 1
                    ? 'Changing this exception:'
                    : `Changing these ${items.length} exceptions:`}
                </Typography>
                <Box
                  component="ul"
                  sx={{ pl: 2.5, m: 0, maxHeight: 180, overflowY: 'auto' }}
                >
                  {items.slice(0, ITEM_PREVIEW_CAP).map((label, i) => (
                    <Typography
                      key={`${label}-${i}`}
                      component="li"
                      variant="body2"
                      sx={{ color: 'text.primary', lineHeight: 1.45 }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>
                {items.length > ITEM_PREVIEW_CAP && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
                  >
                    {`+${items.length - ITEM_PREVIEW_CAP} more`}
                  </Typography>
                )}
                {typeof skippedCount === 'number' && skippedCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                  >
                    {`${skippedCount} selected row${skippedCount === 1 ? '' : 's'} skipped (not eligible)`}
                  </Typography>
                )}
              </Box>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              {copy.bulkDescription}
            </Typography>
          </>
        )}
        <Typography
          component="label"
          htmlFor={inputId}
          variant="body2"
          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
        >
          {copy.inputLabel}
        </Typography>
        <TextField
          id={inputId}
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={5}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={copy.placeholder}
          error={tooLong}
          helperText={
            tooLong
              ? `${reason.length} / ${REASON_MAX} - reason is too long`
              : `${reason.length} / ${REASON_MAX}`
          }
          inputProps={{ 'aria-label': copy.inputLabel }}
          FormHelperTextProps={{
            sx: {
              textAlign: 'right',
              color:
                reason.length >= REASON_WARN_AT && !tooLong
                  ? 'warning.main'
                  : undefined,
            },
          }}
        />
    </BaseDialog>
  )
}

export default ReasonDialog
